import { useEffect, useState } from 'react'
import { command, useConnection, useProject, uploadDocument, projectRepository } from '../app/data'
import { appStore, useAppState } from '../app/state'
import type { Equipment, Role } from '../domain/models'
import type { WorkOrder } from '../domain/operations'
import {
  costsFor,
  costLabels,
  costQualificationLabels,
  money,
  openWork,
  readinessFor,
  readinessOf,
  technicalLabels,
  technicalTone,
  workLabels,
  workResources,
  type Observation,
} from '../domain/readiness'
import { ArtifactViewer, Badge, Modal } from './ui'
import { ActionForm, dateTime, Empty, localDateTime, number, toISO, type Values } from './forms'
import { ObjectRows, Tabs } from './controls'
import { EquipmentForm } from './EquipmentDetail'
import { Attachments } from './Attachments'

export function useTechnicalRole(): Role {
  const { role } = useAppState()
  const c = useConnection()
  const actual = c.session?.appMode === 'production'
    ? c.session.role === 'customer'
      ? 'client'
      : (c.session.role as Role)
    : role
  // Both C profiles have the same technical controls; the server still audits the real user.
  return actual === 'manager' ? 'dispatcher' : actual
}
export const goEquipment = (id: string, role?: Role) => appStore.navigate(role === 'mechanic' ? 'mechanicFleet' : 'fleet', { id })
export const goWork = (id: string) => appStore.navigate('workorders', { id })
export function TechnicalBadge({ equipment }: { equipment: Equipment }) {
  const d = useProject()
  const r = readinessFor(d, equipment)
  return <Badge tone={technicalTone[r.status]}>{technicalLabels[r.status]}</Badge>
}
export function DecisionForm({
  equipment,
  observation,
  onDone,
}: {
  equipment: Equipment
  observation?: Observation
  onDone: () => void
}) {
  const d = useProject()
  const session = useConnection().session
  const own = d.workers.find((w) => w.userId === session?.user.id)
  const activeBlocks = readinessOf(d).blocks.filter((block) => block.equipmentId === equipment.id && !block.clearedAt)
  return (
    <ActionForm
      initial={{
        equipmentId: equipment.id,
        observationId: observation?.id ?? '',
        status: 'diagnostics',
        reason: '',
        responsibleId: own?.id ?? equipment.responsibleId ?? '',
        deadline: localDateTime(new Date(Date.now() + 86400000).toISOString()),
        nextAction: 'Провести диагностику',
        releaseBlockIds: [],
      }}
      fields={(v) => [
        {
          name: 'status',
          label: 'Допустимость эксплуатации',
          type: 'select',
          options: Object.entries(technicalLabels).map(([value, label]) => ({ value, label })),
        },
        { name: 'reason', label: 'Основание технического решения', type: 'textarea', wide: true },
        {
          name: 'responsibleId',
          label: 'Ответственный механик',
          type: 'select',
          options: d.workers.filter((w) => w.userId).map((w) => ({ value: w.id, label: w.name })),
        },
        ...(v.status !== 'ready'
          ? [
              { name: 'nextAction', label: 'Следующее действие' },
              { name: 'deadline', label: 'Срок следующего действия, МСК', type: 'datetime-local' as const },
            ]
          : []),
        ...(v.status === 'limited'
          ? [
              {
                name: 'restrictions',
                label: 'Ограничения и что контролировать',
                type: 'textarea' as const,
                wide: true,
              },
            ]
          : []),
      ]}
      submit={(v, key) =>
        command(
          'readiness.decision',
          '',
          { ...v, deadline: v.status === 'ready' ? '' : toISO(v.deadline) },
          key,
        )
      }
      children={(values, setValue) =>
        activeBlocks.length ? (
          <fieldset className="technical-blocks">
            <legend>Активные основания запрета</legend>
            <p>Снимите только те основания, которые проверены этим решением. Остальные сохранят запрет эксплуатации.</p>
            {activeBlocks.map((block) => {
              const selected = Array.isArray(values.releaseBlockIds) ? values.releaseBlockIds as string[] : []
              return (
                <label key={block.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(block.id)}
                    onChange={(event) => setValue('releaseBlockIds', event.target.checked
                      ? [...selected, block.id]
                      : selected.filter((id) => id !== block.id))}
                  />
                  {block.reason || 'Основание без описания'}
                </label>
              )
            })}
          </fieldset>
        ) : null
      }
      onDone={onDone}
      label="Зафиксировать решение"
    />
  )
}

export function CreateTechnicalWork({
  equipment,
  observation,
  initialKind = 'repair',
  onDone,
}: {
  equipment?: Equipment
  observation?: Observation
  initialKind?: 'repair' | 'maintenance' | 'diagnostics'
  onDone: () => void
}) {
  const d = useProject()
  const session = useConnection().session
  const role = useTechnicalRole()
  const own = d.workers.find((w) => w.userId === session?.user.id)
  return (
    <ActionForm
      initial={{
        equipmentId: equipment?.id ?? '',
        observationId: observation?.id ?? '',
        title: observation?.symptom ?? '',
        kind: initialKind,
        manualStart: role === 'mechanic',
        startReason: '',
        startReasonDetail: '',
        serviceType: '',
        startAt: localDateTime(new Date().toISOString()),
        endAt: localDateTime(new Date(Date.now() + 86400000).toISOString()),
        nextUpdateAt: localDateTime(new Date(Date.now() + 86400000).toISOString()),
        promiseReturn: false,
        promisedReturnAt: localDateTime(new Date(Date.now() + 86400000).toISOString()),
        workerId: own?.id ?? equipment?.responsibleId ?? '',
        operations: [{ name: 'Проверка и выполнение работ по основанию', done: false, result: '' }],
        materials: [],
      }}
      fields={(v) => [
        {
          name: 'equipmentId',
          label: 'Техника',
          type: 'select' as const,
          options: d.equipment.map((e) => ({ value: e.id, label: e.plate + ' · ' + e.model })),
        },
        { name: 'title', label: 'Основание / что выполнить', wide: true },
        ...(role === 'dispatcher'
          ? [
              {
                name: 'kind',
                label: 'Вид воздействия',
                type: 'select' as const,
                options: [
                  { value: 'maintenance', label: 'Плановое ТО' },
                  { value: 'diagnostics', label: 'Диагностика' },
                  { value: 'repair', label: 'Ремонт' },
                ],
              },
            ]
          : []),
        ...(v.kind === 'maintenance'
          ? [{ name: 'serviceType', label: 'Вид ТО (например, ТО-1)', wide: true }]
          : []),
        { name: 'manualStart', label: 'Начать выполнение сейчас', type: 'checkbox' as const, required: false },
        ...(v.manualStart && v.kind === 'maintenance'
          ? [{ name: 'startReason', label: 'Причина начала', type: 'select' as const, options: [
            { value: 'early', label: 'По регламенту досрочно' },
            { value: 'condition', label: 'По состоянию' },
            { value: 'mechanic', label: 'По решению механика' },
            { value: 'other', label: 'Другое' },
          ] }, ...(v.startReason === 'other' ? [{ name: 'startReasonDetail', label: 'Краткая причина', wide: true }] : [])]
          : []),
        ...(role === 'dispatcher' && !v.manualStart
          ? [
              {
                name: 'workerId',
                label: 'Ответственный механик',
                type: 'select' as const,
                options: d.workers.filter((w) => w.userId).map((w) => ({ value: w.id, label: w.name })),
              },
            ]
          : []),
        ...(role === 'dispatcher'
          ? [
              { name: 'startAt', label: 'Начало окна, МСК', type: 'datetime-local' as const },
              { name: 'endAt', label: 'Конец планового окна, МСК', type: 'datetime-local' as const },
              { name: 'nextUpdateAt', label: 'Следующее уточнение срока, МСК', type: 'datetime-local' as const },
              { name: 'promiseReturn', label: 'Срок возврата подтверждён', type: 'checkbox' as const, required: false },
              ...(v.promiseReturn ? [{ name: 'promisedReturnAt', label: 'Обещанный возврат, МСК', type: 'datetime-local' as const }] : []),
              {
                name: 'contractorId',
                label: 'Подрядчик (если нужен)',
                type: 'select' as const,
                required: false,
                options: d.contractors.map((c) => ({ value: c.id, label: c.name })),
              },
            ]
          : []),
      ]}
      submit={(v, key) =>
        command(
          'readiness.work.create',
          '',
          {
            ...v,
            startReason: v.startReason === 'other' ? String(v.startReasonDetail || '').trim() : v.startReason,
            kind: role === 'mechanic' ? initialKind : v.kind,
            assigneeIds: [role === 'mechanic' ? own?.id : v.workerId],
            startAt: v.manualStart ? '' : toISO(v.startAt),
            endAt: v.manualStart ? '' : toISO(v.endAt),
            nextUpdateAt: v.nextUpdateAt ? toISO(v.nextUpdateAt) : undefined,
            promisedReturnAt: v.promiseReturn ? toISO(v.promisedReturnAt) : '',
            materials: (v.materials as Values[]).map((l) => ({
              ...l,
              unitPrice: l.unitPrice === '' ? undefined : l.unitPrice,
            })),
          },
          key,
        )
      }
      onDone={onDone}
      label={role === 'mechanic' && initialKind === 'maintenance' ? 'Начать ТО' : role === 'mechanic' ? 'Создать работу' : 'Создать техническую работу'}
    >
      {(v, set) => (
        <>
          {v.manualStart && v.kind === 'maintenance' && (() => {
            const selected = d.equipment.find((item) => item.id === v.equipmentId)
            if (!selected) return <p className="workspace-note">Выберите машину, затем вид ТО.</p>
            const rule = readinessOf(d).regulations.find((item) => item.id === selected.regulationId && item.active && (!v.serviceType || item.serviceType === v.serviceType))
            const status = readinessFor(d, selected)
            const existing = d.workOrders.find((item) => item.technical && item.kind === 'maintenance' && item.equipmentId === selected.id && openWork(item) && item.serviceType === v.serviceType && !!v.serviceType)
            return <div className="alert" role="status">
              {existing ? <><strong>ТО этого вида уже открыто: {existing.number}</strong><p>Повторное нажатие не создаст вторую работу.</p><button type="button" className="button secondary" onClick={() => goWork(existing.id)}>Открыть текущую работу</button></>
                : <><strong>{!rule || !selected.baselineConfirmed ? 'Срок ТО не рассчитан' : status.remaining != null && status.remaining > 0 ? `ТО досрочно: до порога ${number(status.remaining)} ${status.serviceUnit}` : 'Начало ТО'}</strong>
                  <p>{!rule && 'Регламент не найден. '}{!selected.baselineConfirmed && 'Нет принятого показания. '}Работу можно начать вручную; следующий срок и состав комплекта могут остаться неизвестными. Технический допуск не меняется.</p></>}
            </div>
          })()}
          {role === 'dispatcher' && (
            <ObjectRows
              title="Обязательные операции"
              rows={v.operations as Values[]}
              onChange={(rows) => set('operations', rows)}
              empty={{ name: '', done: false, result: '' }}
              fields={[{ name: 'name', label: 'Операция', wide: true }]}
            />
          )}
          {role === 'dispatcher' && (
            <ObjectRows
              title="Потребность в ресурсах"
              rows={v.materials as Values[]}
              onChange={(rows) => set('materials', rows)}
              empty={{ stockId: '', quantity: 1 }}
              fields={[
                {
                  name: 'stockId',
                  label: 'Материал / запчасть',
                  type: 'select',
                  options: d.consumables.map((s) => ({ value: s.id, label: s.name + ' · ' + s.unit })),
                },
                { name: 'quantity', label: 'Требуется', type: 'number', min: 0.01 },
              ]}
            />
          )}
          <p className="workspace-note">
            {v.manualStart
              ? 'Работа начнётся сразу. Показание, регламент и назначение в диспетчерской не блокируют старт; технический допуск не меняется.'
              : 'Создаётся плановая работа. Окно и ресурсы можно уточнить позднее; технический допуск не меняется.'}
          </p>
        </>
      )}
    </ActionForm>
  )
}

export function ResourceTable({ work }: { work: WorkOrder }) {
  const d = useProject()
  const role = useTechnicalRole()
  const lines = workResources(d, work)
  return lines.length ? (
    <div className="table-wrap">
      {work.regulationUnverified && <p className="alert" role="status">Потребность прежнего автоматического регламента не подтверждена. Руководителю нужно уточнить состав и срок до резерва.</p>}
      {work.materialMode === 'none' && <p className="workspace-note">Материалы не применялись: {work.materialReason || 'причина не указана'}. Нормативный состав сохранён для истории.</p>}
      <table>
        <thead>
          <tr>
            <th>Для этой работы</th>
            <th>План</th>
            <th>Факт</th>
            <th>Свободно*</th>
            <th>Резерв / выдано</th>
            <th>Обеспечение</th>
            <th>Источник / срок</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const actual = work.actualMaterials?.find((line) => line.stockId === l.stockId)
            const purchase = d.purchases.find(
              (item) =>
                item.status !== 'cancelled' &&
                (item.workOrderId === work.id || item.lines.some((line) => line.stockId === l.stockId)),
            )
            return (
              <tr key={l.stockId}>
                <td>
                  {l.name}
                  {role !== 'mechanic' && <small>{l.unitPrice != null ? money(l.unitPrice) + '/ед.' : 'Цена не указана'}</small>}
                </td>
                <td>
                  {number(l.quantity)} {l.unit}
                </td>
                <td>{actual ? `${number(actual.quantity)} ${l.unit}` : 'Не подтверждён'}</td>
                <td>
                  {l.unverified || l.stockMode === 'none' ? (
                    <span>Нет актуальных данных</span>
                  ) : (
                    <>
                      {number(l.available)} {l.unit}
                    </>
                  )}
                </td>
                <td>{number(l.confirmed)} / {number(l.issued)} {l.unit}</td>
                <td>
                  <Badge tone={work.materialMode === 'none' || l.ready ? 'green' : 'amber'}>
                    {work.materialMode === 'none' ? 'Не применялись' : l.unverified
                      ? 'Нужна сверка'
                      : l.missing > 0
                        ? 'Не хватает ' + number(l.missing)
                        : l.ready ? 'Готово' : 'На складе · резерв не подтверждён'}
                  </Badge>
                  {role === 'dispatcher' && openWork(work) && work.materialMode !== 'none' && !l.ready && !l.unverified && l.available > 0 && (
                    <button className="button secondary" type="button" onClick={() =>
                      command('readiness.work.reserve', work.id, {
                        stockId: l.stockId,
                        quantity: l.issued + l.confirmed + Math.min(l.available, Math.max(0, l.quantity - l.issued - l.confirmed)),
                      })
                        .catch((error) => appStore.showToast('Резерв не подтверждён', error instanceof Error ? error.message : 'Ошибка сохранения'))
                    }>
                      Зарезервировать {number(Math.min(l.available, Math.max(0, l.quantity - l.issued - l.confirmed)))} {l.unit}
                    </button>
                  )}
                  {role === 'dispatcher' && openWork(work) && work.materialMode !== 'none' && l.confirmed > 0 && (
                    <button className="button secondary" type="button" onClick={() =>
                      command('readiness.work.reserve', work.id, { stockId: l.stockId, quantity: l.issued })
                        .catch((error) => appStore.showToast('Резерв не снят', error instanceof Error ? error.message : 'Ошибка сохранения'))
                    }>
                      Снять невыданный резерв
                    </button>
                  )}
                  {role === 'dispatcher' && openWork(work) && work.materialMode !== 'none' && l.confirmed > 0 && (
                    <button className="button secondary" type="button" onClick={() =>
                      command('readiness.work.issue', work.id, {
                        stockId: l.stockId,
                        quantity: l.confirmed,
                        comment: 'Выдано для технической работы',
                      })
                        .catch((error) => appStore.showToast('Материал не выдан', error instanceof Error ? error.message : 'Ошибка сохранения'))
                    }>
                      Выдать {number(l.confirmed)} {l.unit}
                    </button>
                  )}
                </td>
                <td>
                  {purchase ? (
                    <>
                      {purchase.supplier || purchase.number}
                      <small>{purchase.neededAt ? dateTime(purchase.neededAt) : 'Срок не указан'}</small>
                    </>
                  ) : (
                    <small>
                      {l.missing > 0
                        ? 'Источник и срок поставки не определены'
                        : `${l.source}${l.sourceUpdatedAt ? ` · ${dateTime(l.sourceUpdatedAt)}` : ''}`}
                    </small>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="workspace-note">* С учётом резервов других работ и назначений.</p>
    </div>
  ) : work.materialMode === 'none' ? (
    <Empty>Материалы не нужны — отсутствие расхода подтверждено.</Empty>
  ) : (
    <Empty>Состав материалов не определён. Уточните потребность в этой работе.</Empty>
  )
}

export function TechnicalWorkDetail({ work }: { work: WorkOrder }) {
  const d = useProject()
  const role = useTechnicalRole()
  const e = d.equipment.find((e) => e.id === work.equipmentId)
  const [refresh, setRefresh] = useState(0)
  const [actionStage, setActionStage] = useState('')
  const [plan, setPlan] = useState(false)
  const [clock, setClock] = useState(Date.now())
  useEffect(() => {
    if (work.kind !== 'repair' || work.awaitingConfirmation || !work.executionIntervals?.some((interval) => !interval.completedAt)) return
    const timer = window.setInterval(() => setClock(Date.now()), 30000)
    return () => window.clearInterval(timer)
  }, [work.kind, work.awaitingConfirmation, work.executionIntervals])
  const activeInterval = work.executionIntervals?.at(-1)
  const elapsed = activeInterval && !activeInterval.completedAt && Number.isFinite(Date.parse(activeInterval.startedAt))
    ? Math.max(0, Math.floor((clock - Date.parse(activeInterval.startedAt)) / 1000)) : 0
  const totalSeconds = Math.max(0, (work.durationSeconds || 0) + elapsed)
  const duration = `${Math.floor(totalSeconds / 3600)} ч ${Math.floor((totalSeconds % 3600) / 60)} мин`
  const mechanicTransitions: Record<string, { value: string; label: string }[]> = {
    planned: [
      { value: 'in_progress', label: 'Начать выполнение' },
      { value: 'waiting', label: 'Ожидать ресурс' },
    ],
    waiting: [{ value: 'in_progress', label: 'Ресурсы получены — начать' }],
    in_progress: work.awaitingConfirmation ? [] : work.kind === 'repair' && work.timingTracked &&
      work.executionIntervals?.length && work.executionIntervals.at(-1)?.completedAt ? [
      { value: 'in_progress', label: 'Возобновить выполнение ремонта' },
    ] : [
      { value: 'control', label: 'Работы выполнены — передать на подтверждение' },
      { value: 'waiting', label: 'Приостановить: нужен ресурс' },
    ],
  }
  const managerTransitions: Record<string, { value: string; label: string }[]> = {
    planned: [{ value: 'cancelled', label: 'Отменить с причиной' }],
    waiting: [{ value: 'cancelled', label: 'Отменить с причиной' }],
    in_progress: work.awaitingConfirmation ? [
      { value: 'completed', label: 'Подтвердить результат и закрыть работу' },
      { value: 'in_progress', label: 'Вернуть на доработку' },
    ] : [],
  }
  const choices = (role === 'mechanic' ? mechanicTransitions : managerTransitions)[work.status] ?? []
  const activeStage = choices.find((choice) => choice.value === actionStage)?.value ?? choices[0]?.value
  return (
    <>
      <div className="readiness-work-header">
        <Badge tone={work.status === 'completed' ? 'green' : 'amber'}>
          {work.awaitingConfirmation ? 'Работы выполнены · требуется подтверждение' : workLabels[work.status]}
        </Badge>
        <button className="button secondary" onClick={() => goEquipment(work.equipmentId, role)}>
          Карточка техники
        </button>
      </div>
      <h3>
        {e?.plate} · {work.title}
      </h3>
      <p>
        Ответственный:{' '}
        {work.assigneeIds.map((id) => d.workers.find((w) => w.id === id)?.name ?? 'Не найден').join(', ') ||
          'Не назначен'}
      </p>
      <p>
        {work.manualStart ? `Начато: ${dateTime(work.startedAt || work.createdAt)}` : `Плановое окно: ${dateTime(work.startAt)} — ${dateTime(work.endAt)}`}
      </p>
      <p>
        {work.promisedReturnAt
          ? `Обещанный возврат: ${dateTime(work.promisedReturnAt)} · подтверждено ${work.promiseConfirmedBy || 'ответственным'}`
          : `Срок возврата неизвестен · следующее уточнение ${dateTime(work.nextUpdateAt || '')}`}
      </p>
      {work.startReason && <p>Основание начала: {work.startReason}</p>}
      {work.kind === 'repair' && work.timingTracked && (
        <p>{activeInterval && !activeInterval.completedAt ? `В работе с ${dateTime(activeInterval.startedAt)} · сейчас ${duration}` : `Время выполнения: ${duration}`} · {work.executionIntervals?.length || 0} интервалов</p>
      )}
      {work.comment && <p className="quiet-card">{work.comment}</p>}
      {!work.technical && (
        <p className="alert">
          Работа из предыдущей версии. История сохранена; для нового контролируемого цикла создайте
          техническую работу.
        </p>
      )}
      <ResourceTable work={work} />
      {work.technical && role === 'dispatcher' && work.materials.length > 0 && (
        <button className="button secondary" type="button" onClick={() =>
          command('document.request', work.id, {}).catch((error) =>
            appStore.showToast('Заявка на материалы не создана', error instanceof Error ? error.message : 'Ошибка сохранения'))
        }>
          Сформировать PDF-заявку на материалы
        </button>
      )}
      {work.technical && <Attachments binding={{ workOrderId: work.id }} readOnly={role !== 'dispatcher'} />}
      {work.technical && ['planned', 'waiting'].includes(work.status) && role === 'dispatcher' && (
        <button className="button secondary" onClick={() => setPlan(true)}>
          Уточнить ресурсы и срок
        </button>
      )}
      {plan && (
        <Modal title="Обеспечение технической работы" onClose={() => setPlan(false)}>
          <ActionForm
            initial={{ endAt: localDateTime(work.endAt || new Date(Date.now() + 86400000).toISOString()), nextUpdateAt: localDateTime(work.nextUpdateAt || new Date(Date.now() + 86400000).toISOString()), promiseReturn: !!work.promisedReturnAt, promisedReturnAt: localDateTime(work.promisedReturnAt || work.endAt || new Date(Date.now() + 86400000).toISOString()), materials: work.materials, comment: '' }}
            fields={(v) => [
              { name: 'endAt', label: 'Конец планового окна, МСК', type: 'datetime-local' },
              { name: 'nextUpdateAt', label: 'Следующее уточнение срока, МСК', type: 'datetime-local' },
              { name: 'promiseReturn', label: 'Срок возврата подтверждён', type: 'checkbox', required: false },
              ...(v.promiseReturn ? [{ name: 'promisedReturnAt', label: 'Обещанный возврат, МСК', type: 'datetime-local' as const }] : []),
              { name: 'comment', label: 'Основание изменения', wide: true },
            ]}
            submit={(v, key) =>
              command(
                'readiness.work.plan',
                work.id,
                {
                  ...v,
                  endAt: toISO(v.endAt),
                  nextUpdateAt: toISO(v.nextUpdateAt),
                  promisedReturnAt: v.promiseReturn ? toISO(v.promisedReturnAt) : '',
                  materials: (v.materials as Values[]).map((l) => ({
                    ...l,
                    unitPrice: l.unitPrice === '' ? undefined : l.unitPrice,
                  })),
                },
                key,
              )
            }
            onDone={() => setPlan(false)}
          >
            {(v, set) => (
              <ObjectRows
                title="Ресурсы и цены"
                rows={v.materials as Values[]}
                onChange={(rows) => set('materials', rows)}
                empty={{ stockId: '', quantity: 1 }}
                fields={[
                  {
                    name: 'stockId',
                    label: 'Ресурс',
                    type: 'select',
                    options: d.consumables.map((s) => ({ value: s.id, label: s.name + ' · ' + s.unit })),
                  },
                  { name: 'quantity', label: 'Количество', type: 'number', min: 0.01 },
                  { name: 'unitPrice', label: 'Цена за единицу, ₽', type: 'number', min: 0, required: false },
                ]}
              />
            )}
          </ActionForm>
        </Modal>
      )}
      {['mechanic', 'dispatcher'].includes(role) && work.technical && choices.length > 0 && (
        <div className="technical-actions" role="group" aria-label="Выберите действие с работой">
          {choices.map((choice) => (
            <button key={choice.value} type="button" className={'button secondary' + (activeStage === choice.value ? ' active' : '')}
              aria-pressed={activeStage === choice.value} onClick={() => setActionStage(choice.value)}>
              {choice.label}
            </button>
          ))}
        </div>
      )}
      {['mechanic', 'dispatcher'].includes(role) && work.technical && choices.length > 0 && (
        <ActionForm
          key={work.status + refresh + activeStage}
          initial={{
            stage: activeStage,
            operations: work.operations ?? [],
            operationsConfirmed: false,
            actualMaterials: [],
            materialMode: '',
            materialReason: '',
            materialsConfirmed: false,
            comment: '',
            finalStatus: 'ready',
            laborRate: work.laborRate ?? '',
            serviceCost: work.serviceCost ?? '',
            deadline: localDateTime(new Date(Date.now() + 86400000).toISOString()),
            nextAction: '',
            restrictions: '',
          }}
          fields={(v) => [
            { name: 'comment', label: v.stage === 'in_progress' ? work.awaitingConfirmation ? 'Причина возврата на доработку' : 'Комментарий (необязательно)' : v.stage === 'waiting' ? 'Какой ресурс ожидается' : 'Результат / причина', type: 'textarea', wide: true, required: v.stage !== 'in_progress' || !!work.awaitingConfirmation },
            ...(v.stage === 'completed'
              ? [
                  {
                    name: 'finalStatus',
                    label: 'Итог проверки',
                    type: 'select' as const,
                    options: [
                      { value: 'ready', label: 'Готовность подтверждена — эксплуатация разрешена' },
                      { value: 'limited', label: 'Допуск с ограничением и последующим контролем' },
                      { value: 'unavailable', label: 'Работа выполнена, техника остаётся недоступной' },
                    ],
                  },
                ]
              : []),
            ...(v.stage === 'completed' && (v.finalStatus === 'limited' || v.finalStatus === 'unavailable')
              ? [
                  ...(v.finalStatus === 'limited' ? [{
                    name: 'restrictions',
                    label: 'Ограничения эксплуатации',
                    type: 'textarea' as const,
                    wide: true,
                  }] : []),
                  { name: 'nextAction', label: 'Что проверить далее', wide: true },
                  {
                    name: 'deadline',
                    label: 'Срок повторной проверки, МСК',
                    type: 'datetime-local' as const,
                  },
                ]
              : []),
            ...(role === 'mechanic' && v.stage === 'control'
              ? [
                  ...(v.materialMode === 'none' || v.materialMode === 'deviation'
                    ? [{ name: 'materialReason', label: 'Причина отсутствия или отличия расхода', type: 'textarea' as const, wide: true }]
                    : []),
                  { name: 'materialsConfirmed', label: 'Подтверждаю фактически использованное количество', type: 'checkbox' as const },
                ]
              : []),
          ]}
          submit={(v, key) =>
            command(
              'readiness.work.progress',
              work.id,
              {
                ...v,
                laborHours: undefined,
                endAt: '',
                deadline: v.stage === 'completed' && (v.finalStatus === 'limited' || v.finalStatus === 'unavailable') ? toISO(v.deadline) : '',
                actualMaterials: ((v.actualMaterials as Values[] | undefined) ?? []).map((line) => ({
                  ...line,
                  quantity: Number(line.quantity),
                  unitPrice:
                    line.unitPrice == null || line.unitPrice === '' ? undefined : Number(line.unitPrice),
                })),
                laborRate: undefined,
                serviceCost: undefined,
              },
              key,
            )
          }
          onDone={() => setRefresh((x) => x + 1)}
          label={activeStage === 'in_progress' ? work.awaitingConfirmation ? 'Вернуть на доработку' : 'Начать выполнение' : activeStage === 'waiting' ? 'Отметить ожидание' : activeStage === 'control' ? 'Передать результат на подтверждение' : activeStage === 'completed' ? 'Подтвердить результат и решение' : 'Отменить работу'}
        >
          {(v, set) =>
            v.stage === 'control' && role === 'mechanic' && (
              <section className="technical-checklist">
                <h3>Выполнение операций</h3>
                {!!work.regulationId && <label>
                  <input
                    type="checkbox"
                    checked={Boolean(v.operationsConfirmed)}
                    onChange={(event) => set('operationsConfirmed', event.target.checked)}
                  />
                  Все операции выполнены по регламенту без отклонений
                </label>}
                {(!v.operationsConfirmed || !work.regulationId) && (
                  <>
                    <p className="workspace-note">При отклонении заполните результат каждой операции.</p>
                    {(v.operations as Values[]).map((op, i) => (
                      <div className="technical-operation" key={i}>
                        <label>
                          <input
                            type="checkbox"
                            checked={!!op.done}
                            onChange={(event) => set('operations', (v.operations as Values[]).map((item, j) =>
                              j === i ? { ...item, done: event.target.checked } : item,
                            ))}
                          />
                          {String(op.name)}
                        </label>
                        <input
                          aria-label={'Результат операции ' + (i + 1)}
                          value={String(op.result)}
                          placeholder="Фактический результат"
                          onChange={(event) => set('operations', (v.operations as Values[]).map((item, j) =>
                            j === i ? { ...item, result: event.target.value } : item,
                          ))}
                        />
                      </div>
                    ))}
                  </>
                )}
                {role === 'mechanic' && v.stage === 'control' && (
                  <div>
                    <h3>Что фактически использовано?</h3>
                    <div className="technical-actions">
                      {[
                        { value: 'norm', label: 'Всё по регламенту' },
                        { value: 'deviation', label: 'Есть отличия' },
                        { value: 'none', label: 'Ничего не использовано' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={'button secondary' + (v.materialMode === option.value ? ' active' : '')}
                          aria-pressed={v.materialMode === option.value}
                          onClick={() => {
                            set('materialMode', option.value)
                            set('materialsConfirmed', false)
                            set('actualMaterials', option.value === 'none'
                              ? []
                              : work.materials.map((line) => ({ stockId: line.stockId, quantity: line.quantity })))
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {(v.materialMode === 'norm' || v.materialMode === 'deviation') && (
                      <ObjectRows
                        title="Фактически использованные материалы"
                        rows={(v.actualMaterials as Values[] | undefined) ?? []}
                        onChange={(rows) => { set('actualMaterials', rows); set('materialsConfirmed', false) }}
                        empty={{ stockId: '', quantity: 1 }}
                        fields={[
                          {
                            name: 'stockId',
                            label: 'Материал',
                            type: 'select',
                            options: d.consumables.map((stock) => ({
                              value: stock.id,
                              label: `${stock.name} · ${stock.unit}`,
                            })),
                          },
                          { name: 'quantity', label: 'Факт', type: 'number', min: 0.01 },
                        ]}
                      />
                    )}
                  </div>
                )}
                <p className="workspace-note">
                  При отклонениях незавершённые операции не позволяют передать работу на контроль. Допуск техники изменится только после подтверждения.
                </p>
              </section>
            )
          }
        </ActionForm>
      )}
      {work.status === 'completed' && (
        <div className="quiet-card">
          <strong>{work.result}</strong>
          <p>
            Итог: {technicalLabels[work.finalStatus as keyof typeof technicalLabels] ?? 'Не подтверждён'}.{' '}
            {role !== 'mechanic' && (work.costComplete
              ? 'Состав затрат работы заполнен.'
              : 'Стоимость работы неполная: проверьте цены, труд и услуги.')}
          </p>
        </div>
      )}
    </>
  )
}

export function AvailabilityReasons({ equipment: e, onBack }: { equipment: Equipment; onBack: () => void }) {
  const d = useProject()
  const state = readinessOf(d)
  const r = readinessFor(d, e)
  const [history, setHistory] = useState(false)
  const blocks = state.blocks
    .filter((block) => block.equipmentId === e.id && Boolean(block.clearedAt) === history)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
  const pendingStops = history ? [] : state.observations.filter(
    (observation) => observation.equipmentId === e.id && observation.state === 'new' && observation.canContinue === 'no',
  )
  return (
    <div className="availability-reasons">
      <div className="availability-heading">
        <button className="button secondary" type="button" onClick={onBack}>К машине</button>
        <TechnicalBadge equipment={e} />
      </div>
      <p className="workspace-note">Готовность определяется всеми открытыми основаниями. Просмотр этой страницы её не изменяет.</p>
      <Tabs value={history ? 'history' : 'current'} onChange={(value) => setHistory(value === 'history')} items={[
        { value: 'current', label: 'Сейчас', count: state.blocks.filter((block) => block.equipmentId === e.id && !block.clearedAt).length + pendingStops.length },
        { value: 'history', label: 'История', count: state.blocks.filter((block) => block.equipmentId === e.id && !!block.clearedAt).length },
      ]} />
      <div className="availability-list">
        {blocks.map((block) => {
          const decision = state.decisions.find((item) => item.id === block.decisionId)
          const work = d.workOrders.find((item) => item.id === block.workOrderId)
          const observation = state.observations.find((item) => item.id === block.observationId)
          const responsible = d.workers.find((item) => item.id === decision?.responsibleId)?.name || 'Не назначен'
          return (
            <article className="availability-card" key={block.id}>
              <div className="availability-card-title">
                <strong>{block.reason || 'Основание не указано'}</strong>
                <Badge tone={block.clearedAt ? 'green' : 'red'}>{block.clearedAt ? 'Закрыта' : 'Открыта'}</Badge>
              </div>
              <dl className="availability-meta">
                <div><dt>Начало</dt><dd>{block.createdAt ? dateTime(block.createdAt) : 'Нет данных'}</dd></div>
                <div><dt>Ответственный</dt><dd>{responsible}</dd></div>
                <div><dt>Ожидаемый возврат</dt><dd>{work?.promisedReturnAt ? dateTime(work.promisedReturnAt) : decision?.deadline ? dateTime(decision.deadline) : 'Не указан'}</dd></div>
                {block.clearedAt && <div><dt>Закрыта</dt><dd>{dateTime(block.clearedAt)}</dd></div>}
              </dl>
              {(work || observation) && <div className="technical-actions">
                {work && <button className="button secondary" type="button" onClick={() => goWork(work.id)}>Открыть работу {work.number}</button>}
                {observation && <button className="button secondary" type="button" onClick={() => appStore.navigate('workorders', { observation: observation.id })}>Открыть сообщение</button>}
              </div>}
            </article>
          )
        })}
        {pendingStops.map((observation) => (
          <article className="availability-card" key={'observation-' + observation.id}>
            <div className="availability-card-title"><strong>Оператор сообщил: продолжать работу нельзя</strong><Badge tone="amber">Ожидает оценки</Badge></div>
            <p>{observation.symptom}</p>
            <dl className="availability-meta"><div><dt>Сообщено</dt><dd>{dateTime(observation.createdAt)}</dd></div><div><dt>Ответственный</dt><dd>{r.responsible}</dd></div></dl>
            <button className="button secondary" type="button" onClick={() => appStore.navigate('workorders', { observation: observation.id })}>Открыть сообщение</button>
          </article>
        ))}
        {!history && !blocks.length && !pendingStops.length && r.permission !== 'ready' && (
          <article className="availability-card">
            <strong>{e.technicalReason || technicalLabels[r.status]}</strong>
            <p>Отдельное основание не зарегистрировано. Требуется техническая оценка.</p>
          </article>
        )}
        {!blocks.length && !pendingStops.length && (history || r.permission === 'ready') && <Empty>{history ? 'Закрытых причин нет' : 'Открытых причин нет'}</Empty>}
      </div>
    </div>
  )
}

export function EquipmentOverview({ equipment: e, onAvailability }: { equipment: Equipment; onAvailability?: () => void }) {
  const d = useProject()
  const r = readinessFor(d, e)
  const role = useTechnicalRole()
  const [tab, setTab] = useState('overview')
  const [action, setAction] = useState('')
  const now = new Date().toISOString().slice(0, 10)
  const cost = costsFor(d, e.id, now.slice(0, 7) + '-01', now)
  const meters = e.meters?.length
    ? e.meters.filter((meter) => meter.active !== false)
    : [
        {
          id: 'primary',
          name: 'Основной счётчик',
          unit: e.meterUnit || 'мч',
          value: e.meter,
          readingAt: e.readingAt || '',
          source: e.sourceSystem || 'Ручной ввод',
          active: true,
          primary: true,
        },
      ]
  const primaryMeter = meters.find((meter) => meter.primary) ?? meters[0]
  const readings = readinessOf(d).readings.filter((reading) => reading.equipmentId === e.id)
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt) || b.id.localeCompare(a.id))
  const maintenanceWorks = d.workOrders.filter((work) => work.equipmentId === e.id && work.kind === 'maintenance')
  const regulation = readinessOf(d).regulations.find((item) => item.id === e.regulationId && item.active)
  return (
    <>
      <div className="technical-hero">
        {e.archivedAt && <Badge tone="amber">Архив · {dateTime(e.archivedAt)}</Badge>}
        <TechnicalBadge equipment={e} />
        <h2>{e.model}</h2>
        <p>
          {e.technicalReason ||
            'Нет подтверждённого технического решения. Операционный статус не является допуском.'}
        </p>
        <small>
          {e.technicalAt
            ? 'Подтверждено ' +
              dateTime(e.technicalAt) +
              ' · ' +
              (d.workers.find((w) => w.userId === e.technicalBy)?.name ?? e.technicalBy)
            : 'Требуется техническая оценка'}
        </small>
      </div>
      <div className="technical-kpis">
        <div>
          <span>Принятая наработка</span>
          <strong>
            {e.baselineConfirmed ? number(e.meter) + ' ' + (e.meterUnit || 'мч') : 'Не подтверждена'}
          </strong>
          <small>
            {e.readingAt ? dateTime(e.readingAt) : 'Дата показания неизвестна'}
            {r.stale ? ' · неактуально' : ''}
          </small>
        </div>
        <div>
          <span>До {r.serviceDue}</span>
          <strong>{r.remaining == null ? 'Не рассчитано' : number(r.remaining) + ' ' + r.serviceUnit}</strong>
          <small>
            {r.forecastDate
              ? `Прогноз: ${r.forecastDate} · по принятым показаниям за 30 дней`
              : 'Прогноз недоступен: нужны два принятых показания за 30 дней с интервалом от суток'}
          </small>
        </div>
        <div>
          <span>Ответственный</span>
          <strong>{r.responsible}</strong>
          <small>
            {r.decision?.deadline ? 'Следующий шаг: ' + dateTime(r.decision.deadline) : 'Срок не установлен'}
          </small>
        </div>
      </div>
      <div className="technical-actions">
        <Badge tone={r.issues.length ? 'amber' : 'green'}>Активных неисправностей: {r.issues.length}</Badge>
        {onAvailability && <button className="button secondary" type="button" onClick={onAvailability}>Причины готовности и история</button>}
        <button className="button secondary" onClick={() => {
          setTab('overview')
          window.requestAnimationFrame(() => document.getElementById('technical-active-works-' + e.id)?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
        }}>
          Текущие ТОиР: {r.works.length}
        </button>
        {['manager', 'dispatcher'].includes(role) && (
          <button className="button secondary" onClick={() => setTab('cost')}>
            Затраты за месяц: {cost.hasData ? money(cost.total) : 'не внесены'} ·{' '}
            {cost.missing.length ? 'неполный расчёт' : 'проверить полноту'}
          </button>
        )}
      </div>
      <p className="workspace-note">
        Операционный контекст:{' '}
        {e.status === 'working'
          ? 'На объекте'
          : e.status === 'base'
            ? 'На базе'
            : 'Историческая отметка «На ТО»'}{' '}
        · {e.location || 'Место не задано'}. Источник: {e.sourceSystem || 'Ручной ввод'}
        {e.sourceUpdatedAt ? ` · обновлено ${dateTime(e.sourceUpdatedAt)}` : ' · время обновления не указано'}
        .
      </p>
      {r.conflict && (
        <p className="alert">
          Конфликт с СД: машина назначена на {dateTime(r.assignment!.startAt)}, но техническая готовность не
          подтверждена. Назначение автоматически не меняется.
        </p>
      )}
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'overview', label: 'Обзор' },
          ...(role !== 'mechanic' ? [{ value: 'passport', label: 'Паспорт' }] : []),
          { value: 'history', label: 'История' },
          { value: 'readings', label: 'Показания', count: readings.length },
          { value: 'maintenance', label: 'ТО', count: maintenanceWorks.filter(openWork).length },
          { value: 'materials', label: 'Материалы' },
          ...(role !== 'mechanic' ? [{ value: 'documents', label: 'Документы' }] : []),
          ...(role === 'manager' || role === 'dispatcher' ? [{ value: 'cost', label: 'Затраты' }] : []),
        ]}
      />
      {tab === 'overview' && (
        <>
          <h3>Что требует действия</h3>
          {r.reasons.length ? (
            <ul>
              {r.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p>Активных технических проблем не зарегистрировано.</p>
          )}
          {r.decision?.restrictions && <p className="alert">Ограничения: {r.decision.restrictions}</p>}
          <p>
            <strong>Следующий шаг: </strong>
            {r.decision?.nextAction || r.next}
          </p>
          <h3>Счётчики техники</h3>
          <div className="technical-kpis">
            {meters.map((meter) => (
              <div key={meter.id}>
                <span>{meter.name}</span>
                <strong>
                  {number(meter.value)} {meter.unit}
                </strong>
                <small>
                  {meter.readingAt ? dateTime(meter.readingAt) : 'Дата не указана'} ·{' '}
                  {meter.source || 'Источник не указан'}
                </small>
              </div>
            ))}
          </div>
          <div className="technical-actions">
            {role === 'dispatcher' && !e.archivedAt && (
              <button className="button primary" onClick={() => setAction('decision')}>
                Техническое решение
              </button>
            )}
            {(role === 'mechanic' || role === 'dispatcher') && !e.archivedAt && <>
              <button className="button secondary" onClick={() => setAction('maintenance')}>Начать ТО</button>
              <button className="button secondary" onClick={() => setAction('repair')}>Начать ремонт</button>
              <button className="button secondary" onClick={() => setAction('diagnostics')}>Диагностика</button>
            </>}
            {role === 'dispatcher' && !e.archivedAt && (
              <button className="button secondary" onClick={() => setAction('assign')}>
                Назначить ответственного
              </button>
            )}
          </div>
          <h3 id={'technical-active-works-' + e.id}>Активные работы</h3>
          {r.works.map((work) => (
            <button className="technical-work-row" key={work.id} onClick={() => goWork(work.id)}>
              <strong>{work.number} · {work.title}</strong>
              <Badge>{work.awaitingConfirmation ? 'Ожидает подтверждения' : workLabels[work.status]}</Badge>
              <span>{dateTime(work.endAt)}</span>
            </button>
          ))}
          {!r.works.length && <Empty>Активных работ нет</Empty>}
          {r.issues.map((o) => (
            <div className="technical-observation" key={o.id}>
              <Badge tone="amber">{o.state === 'new' ? 'Наблюдение оператора' : 'Решение принято'}</Badge>
              <h3>{o.symptom}</h3>
              <p>{o.description}</p>
              <small>
                {dateTime(o.createdAt)} · {o.actor}
              </small>
              <button
                className="button secondary"
                onClick={() => appStore.navigate('workorders', { observation: o.id })}
              >
                Открыть ситуацию
              </button>
            </div>
          ))}
        </>
      )}
      {tab === 'readings' && (
        <>
          <div className="section-heading">
            <div><h3>История показаний</h3><p>Принятые факты и аномалии на проверке</p></div>
            {!e.archivedAt && <button className="button primary" onClick={() => setAction('reading')}>Добавить показание</button>}
          </div>
          {readings.map((reading) => <div className="quiet-card reading-card" key={reading.id}>
            <strong>{number(reading.value)} {reading.unit || e.meterUnit || 'мч'} · {reading.meterName || 'Основной счётчик'}</strong>
            <p>{dateTime(reading.recordedAt)} · {reading.status === 'accepted' ? 'Принято' : reading.status === 'rejected' ? 'Отклонено' : 'Требуется проверка'}</p>
            <small>Источник: {reading.source || 'Не указан'} · Автор: {reading.actor}</small>
            {reading.reason && <p>{reading.reason}</p>}
            {reading.comment && <p>{reading.comment}</p>}
            {!!reading.photoIds?.length && <EvidenceViewer ids={reading.photoIds} />}
          </div>)}
          {!readings.length && <Empty>Принятых показаний ещё нет. Укажите фактическое значение и время — это не изменит технический допуск.</Empty>}
        </>
      )}
      {tab === 'maintenance' && (
        <>
          <h3>Регламент и ближайшее ТО</h3>
          <p>{regulation ? `${regulation.name} · ${regulation.serviceType}` : 'Подтверждённый регламент не задан'}</p>
          <p>{r.remaining == null ? 'Срок по наработке не рассчитан — ручное планирование доступно.' : `До ${r.serviceDue}: ${number(r.remaining)} ${r.serviceUnit}`}</p>
          {r.forecastDate && <p>Прогноз даты: {r.forecastDate}</p>}
          {!e.archivedAt && <button className="button primary" onClick={() => setAction('maintenance')}>Начать ТО</button>}
          {maintenanceWorks.map((work) => <button className="technical-work-row" key={work.id} onClick={() => goWork(work.id)}>
            <strong>{work.number} · {work.title}</strong><Badge>{workLabels[work.status]}</Badge><span>{dateTime(work.startAt)}</span>
          </button>)}
          {!maintenanceWorks.length && <Empty>Работ ТО по этой машине пока нет</Empty>}
          {role === 'dispatcher' && (
            <ActionForm
              initial={{
                interval: e.interval,
                intervalTO1: e.intervalTO1,
                intervalTO2: e.intervalTO2,
                alertBefore: e.alertBefore,
                criticalBefore: e.criticalBefore,
              }}
              fields={[
                ...(e.meterUnit === 'км'
                  ? [{ name: 'interval', label: 'Интервал сервиса, км', type: 'number' as const, min: 1 }]
                  : [
                      { name: 'intervalTO1', label: 'Интервал ТО-1, мч', type: 'number' as const, min: 1 },
                      { name: 'intervalTO2', label: 'Интервал ТО-2, мч', type: 'number' as const, min: 1 },
                    ]),
                {
                  name: 'alertBefore',
                  label: 'Предупреждать за, ' + (e.meterUnit || 'мч'),
                  type: 'number',
                  min: 0,
                },
                {
                  name: 'criticalBefore',
                  label: 'Критичный порог, ' + (e.meterUnit || 'мч'),
                  type: 'number',
                  min: 0,
                },
              ]}
              submit={(v, key) => command('equipment.service', e.id, v, key)}
              label="Сохранить индивидуальное исключение"
            />
          )}
          {role === 'dispatcher' && !e.baselineConfirmed && <BaselineSetup equipment={e} />}
        </>
      )}
      {tab === 'materials' && <>
        <h3>Материалы по активным работам</h3>
        {r.works.map((work) => <section key={work.id}>
          <button className="technical-work-row" onClick={() => goWork(work.id)}><strong>{work.number} · {work.title}</strong><span>Открыть работу →</span></button>
          <ResourceTable work={work} />
        </section>)}
        {!r.works.length && <Empty>Активных работ нет — потребность в материалах не назначена</Empty>}
      </>}
      {tab === 'history' && (
        <>
          <h3>Периоды технической недоступности</h3>
          {readinessOf(d)
            .downtimes.filter((p) => p.equipmentId === e.id)
            .map((p) => (
              <div className="quiet-card" key={p.id}>
                {dateTime(p.startAt)} — {p.endAt ? dateTime(p.endAt) : 'продолжается'}
                <p>
                  {number(
                    Math.max(
                      0,
                      (Date.parse(p.endAt || new Date().toISOString()) - Date.parse(p.startAt)) / 3600000,
                    ),
                  )}{' '}
                  ч · основание — техническое решение руководителя механизации
                </p>
              </div>
            ))}
          <TechnicalHistory equipmentId={e.id} />
        </>
      )}
      {tab === 'documents' && <Attachments binding={{ equipmentId: e.id }} readOnly={role === 'manager'} />}
      {tab === 'cost' && (
        <>
          <h3>Текущий месяц: {cost.hasData ? money(cost.total) : 'Нет данных о затратах'}</h3>
          <p className="workspace-note">
            {cost.missing.length
              ? 'Расчёт неполный: ' + cost.missing.join(', ')
              : 'Учтены внесённые категории. Проверьте полноту первичных данных.'}
          </p>
          <CostEntries entries={cost.entries} />
          <button
            className="button secondary"
            onClick={() => appStore.navigate('costs', { equipment: e.id })}
          >
            Стоимость за период
          </button>
        </>
      )}
      {tab === 'passport' && (
        <>
          <dl className="detail-grid">
            {Object.entries({
              Госномер: e.plate,
              VIN: e.vin,
              'Гаражный номер': e.garageNumber,
              Год: e.year || 'Не указан',
              Тип: e.type,
              'Нормализованный вид': e.typeKey,
              'Исходный вид': e.sourceTypeName,
              'Код вида в источнике': e.sourceTypeId,
              'Состояние сопоставления':
                (
                  {
                    recognized: 'Распознано',
                    review: 'Требует проверки',
                    manual: 'Сопоставлено вручную',
                  } as Record<string, string>
                )[e.mappingStatus || ''] || 'Не указано',
              'Источник карточки': e.sourceSystem,
              'Внешний идентификатор': e.externalId,
              'Данные источника обновлены': e.sourceUpdatedAt ? dateTime(e.sourceUpdatedAt) : 'Не указано',
              Подразделение: e.department,
              'Закреплённый оператор': e.operator,
              Сезонность:
                (
                  {
                    year_round: 'Круглогодичная',
                    winter: 'Зимняя',
                    summer: 'Летняя',
                    period: 'По периоду',
                  } as Record<string, string>
                )[e.season] || 'Круглогодичная',
              ...(e.season === 'period'
                ? { 'Период эксплуатации': `${e.seasonStart || '—'} — ${e.seasonEnd || '—'}` }
                : {}),
            }).map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value || 'Не указано'}</dd>
              </div>
            ))}
          </dl>
          {role === 'dispatcher' && !e.archivedAt && (
            <button className="button secondary" onClick={() => setAction('passport')}>
              Редактировать паспорт
            </button>
          )}
          {role === 'dispatcher' && !e.archivedAt && (
            <button className="button secondary" onClick={() => setAction('archive')}>
              Перевести в архив
            </button>
          )}
        </>
      )}
      {action && (
        <Modal
          title={
            action === 'decision'
              ? 'Техническое решение'
              : ['maintenance', 'repair', 'diagnostics'].includes(action)
                ? action === 'maintenance' ? 'Начать ТО' : action === 'diagnostics' ? 'Диагностика' : 'Начать ремонт'
                : action === 'reading'
                  ? 'Добавить показание'
                : action === 'assign'
                  ? 'Ответственность за машину'
                : action === 'archive'
                  ? 'Архивировать технику'
                  : 'Паспорт техники'
          }
          onClose={() => setAction('')}
        >
          {action === 'decision' && <DecisionForm equipment={e} onDone={() => setAction('')} />}
          {(['maintenance', 'repair', 'diagnostics'] as const).map((kind) => action === kind &&
            <CreateTechnicalWork key={kind} equipment={e} initialKind={kind} onDone={() => setAction('')} />)}
          {action === 'reading' && <ActionForm
            draftKey={'equipment-reading-' + e.id}
            initial={{ equipmentId: e.id, meterId: primaryMeter?.id || 'primary', value: '', recordedAt: localDateTime(new Date().toISOString()), photoIds: [], comment: '' }}
            fields={(values) => {
              const selectedMeter = meters.find((meter) => meter.id === values.meterId) ?? primaryMeter
              return [
                ...(meters.length > 1 ? [{ name: 'meterId', label: 'Счётчик', type: 'select' as const, options: meters.map((meter) => ({ value: meter.id, label: `${meter.name} · ${meter.unit}` })) }] : []),
                { name: 'value', label: 'Новое показание, ' + (selectedMeter?.unit || e.meterUnit || 'мч'), type: 'number' as const, min: 0, max: 9999999.9, step: 0.1 },
                { name: 'recordedAt', label: 'Фактическое время, МСК', type: 'datetime-local' as const },
                { name: 'comment', label: 'Комментарий', type: 'textarea' as const, required: false, wide: true },
              ]
            }}
            submit={(values, key) => command('readiness.reading', '', { ...values, value: Number(values.value), recordedAt: toISO(values.recordedAt) }, key)}
            onDone={() => setAction('')}
            label="Сохранить показание"
          >{(values, set) => {
            const selectedMeter = meters.find((meter) => meter.id === values.meterId) ?? primaryMeter
            const previousAt = selectedMeter?.readingAt || (selectedMeter?.primary ? e.readingAt : '')
            const previousValue = selectedMeter?.primary && e.readingAt ? e.meter : selectedMeter?.value
            return <>
              <p className="workspace-note">{e.plate} · {e.model}. Прежнее принятое показание: {previousAt ? `${number(previousValue || 0)} ${selectedMeter?.unit || e.meterUnit || 'мч'} · ${dateTime(previousAt)}` : 'нет подтверждённого показания'}. Новый факт не подтверждает готовность техники.</p>
              <PhotoEvidence equipmentId={e.id} selected={values.photoIds as string[]} onChange={(ids) => set('photoIds', ids)} />
            </>
          }}</ActionForm>}
          {action === 'passport' && <EquipmentForm equipment={e} onDone={() => setAction('')} />}
          {action === 'archive' && (
            <ActionForm
              initial={{ reason: '' }}
              fields={[{ name: 'reason', label: 'Причина архивирования', type: 'textarea', wide: true }]}
              submit={(values, key) => command('equipment.archive', e.id, values, key)}
              onDone={() => setAction('')}
              label="Архивировать без удаления истории"
            />
          )}
          {action === 'assign' && (
            <ActionForm
              initial={{ equipmentId: e.id, workerId: e.responsibleId ?? '', operatorId: e.operatorId }}
              fields={[
                {
                  name: 'workerId',
                  label: 'Ответственный механик',
                  type: 'select',
                  options: d.workers.filter((w) => w.userId).map((w) => ({ value: w.id, label: w.name })),
                },
                {
                  name: 'operatorId',
                  label: 'Закреплённый оператор',
                  type: 'select',
                  required: false,
                  options: d.operators.map((o) => ({ value: o.id, label: o.name })),
                },
              ]}
              submit={(v, key) => command('readiness.assign', '', v, key)}
              onDone={() => setAction('')}
            />
          )}
        </Modal>
      )}
    </>
  )
}

function BaselineSetup({ equipment: e }: { equipment: Equipment }) {
  return (
    <details className="quiet-card">
      <summary>Подтвердить исходный регламент и показание</summary>
      <p>
        Требуется сверка с техникой и регламентом. Это не технический допуск и не свежее полевое показание.
      </p>
      <ActionForm
        initial={{
          meter: e.meter,
          meterUnit: e.meterUnit || '',
          dueTO1: e.dueTO1 || 500,
          dueTO2: e.dueTO2 || 2000,
          due: e.due,
          interval: e.interval,
          comment: '',
          confirmed: false,
        }}
        fields={(v) => [
          { name: 'meter', label: 'Исходное показание', type: 'number', min: 0 },
          {
            name: 'meterUnit',
            label: 'Единица счётчика',
            type: 'select',
            required: false,
            options: [
              { value: '', label: 'Моточасы' },
              { value: 'км', label: 'Километры' },
            ],
          },
          ...(v.meterUnit === 'км'
            ? [
                { name: 'due', label: 'Отметка следующего сервиса, км', type: 'number' as const, min: 1 },
                { name: 'interval', label: 'Интервал сервиса, км', type: 'number' as const, min: 1 },
              ]
            : [
                { name: 'dueTO1', label: 'Отметка следующего ТО-1, мч', type: 'number' as const, min: 1 },
                { name: 'dueTO2', label: 'Отметка следующего ТО-2, мч', type: 'number' as const, min: 1 },
              ]),
          { name: 'comment', label: 'Основание сверки', type: 'textarea', wide: true },
          { name: 'confirmed', label: 'Регламент и показание проверены', type: 'checkbox' },
        ]}
        submit={(v, key) => command('equipment.baseline', e.id, v, key)}
        label="Подтвердить исходные данные"
      />
    </details>
  )
}

export function CostEntries({ entries }: { entries: ReturnType<typeof costsFor>['entries'] }) {
  return entries.length ? (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Дата</th>
            <th>Классификация</th>
            <th>Назначение и источник</th>
            <th>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((c) => (
            <tr key={c.id}>
              <td>
                {c.date}
                <small>{c.documentNumber || 'Без номера документа'}</small>
              </td>
              <td>
                {costLabels[c.category] || c.category}
                <small>
                  {costQualificationLabels[c.qualification] || c.qualification || 'Не квалифицирован'}
                </small>
              </td>
              <td>
                {c.workOrderId ? (
                  <button className="link-button" onClick={() => goWork(c.workOrderId)}>
                    {c.description}
                  </button>
                ) : (
                  c.description
                )}
                <small>
                  {[c.source, c.counterparty].filter(Boolean).join(' · ') || 'Источник не указан'} · Автор:{' '}
                  {c.actor}
                </small>
              </td>
              <td>{money(c.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Empty>Расходы за период не внесены — это не нулевая стоимость содержания.</Empty>
  )
}

export function TechnicalHistory({ equipmentId = '' }: { equipmentId?: string }) {
  const d = useProject()
  const [type, setType] = useState('')
  const actions: Record<string, string> = {
    'readiness.observation': 'Сообщение оператора',
    'readiness.decision': 'Техническое решение',
    'readiness.changed': 'Изменение готовности',
    'readiness.reading': 'Показание',
    'readiness.reading.review': 'Проверка показания',
    'readiness.work.create': 'Создание работы',
    'readiness.work.plan': 'Обеспечение и срок работы',
    'readiness.work.progress': 'Этап технической работы',
    'readiness.cost': 'Управленческий расход',
    'readiness.assign': 'Ответственность',
    'readiness.shift': 'Смена',
  }
  const records = d.events
    .filter(
      (ev) =>
        (!equipmentId ||
          ev.entityId === equipmentId ||
          d.workOrders.some((w) => w.id === ev.entityId && w.equipmentId === equipmentId)) &&
        (!type || ev.action === type),
    )
    .slice()
    .reverse()
  return (
    <>
      <select
        className="field-select"
        aria-label="Тип события истории"
        value={type}
        onChange={(ev) => setType(ev.target.value)}
      >
        <option value="">Все события</option>
        {Object.entries(actions).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <div className="technical-history">
        {records.map((ev) => (
          <article key={ev.id}>
            <small>
              {dateTime(ev.createdAt)} · {ev.actor}
            </small>
            <strong>{actions[ev.action] ?? 'Событие учётного контура'}</strong>
            <p>{ev.detail || 'Исходная запись сохранена'}</p>
            {d.workOrders.some((w) => w.id === ev.entityId) && (
              <button className="link-button" onClick={() => goWork(ev.entityId)}>
                Открыть работу
              </button>
            )}
          </article>
        ))}
      </div>
      {!records.length && <Empty />}
    </>
  )
}

export function EvidenceViewer({ ids }: { ids: string[] }) {
  const d = useProject()
  const [selected, setSelected] = useState('')
  const photos = d.artifacts.filter((a) => ids.includes(a.id))
  const photo = photos.find((a) => a.id === selected)
  return (
    <div className="technical-actions">
      {photos.map((a) => (
        <button key={a.id} className="button secondary" onClick={() => setSelected(a.id)}>
          Фото · {a.name}
        </button>
      ))}
      {photo && (
        <Modal title={photo.name} onClose={() => setSelected('')}>
          <ArtifactViewer artifact={photo} />
        </Modal>
      )}
    </div>
  )
}

export function PhotoEvidence({
  equipmentId,
  selected,
  onChange,
}: {
  equipmentId: string
  selected: string[]
  onChange: (ids: string[]) => void
}) {
  const d = useProject()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const photos = d.artifacts.filter((a) => a.equipmentId === equipmentId && a.mime?.startsWith('image/'))
  return (
    <section>
      <h3>Фото события</h3>
      <label className="upload-zone">
        <span>{busy ? 'Загрузка фото…' : 'Прикрепить фото PNG/JPEG, до 8 МБ'}</span>
        <input
          type="file"
          accept="image/png,image/jpeg"
          capture="environment"
          aria-label="Фото события"
          disabled={busy || !equipmentId}
          onChange={async (ev) => {
            const file = ev.target.files?.[0]
            if (!file) return
            setBusy(true)
            setError('')
            const known = new Set(d.artifacts.map((a) => a.id))
            try {
              await uploadDocument(file, 'Полевое событие', '', { equipmentId })
              const added = projectRepository
                .getSnapshot()
                .artifacts.find((a) => !known.has(a.id) && a.equipmentId === equipmentId)
              if (added) onChange([...selected, added.id])
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Ошибка загрузки')
            } finally {
              setBusy(false)
            }
          }}
        />
      </label>
      {error && <p role="alert">{error}</p>}
      {photos.map((a) => (
        <label className="photo-choice" key={a.id}>
          <input
            type="checkbox"
            checked={selected.includes(a.id)}
            onChange={(ev) =>
              onChange(ev.target.checked ? [...selected, a.id] : selected.filter((id) => id !== a.id))
            }
          />
          {a.name}
        </label>
      ))}
    </section>
  )
}
